import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';

// I had quite a bit of trouble tracking image loading reliably. This seems to work.
// My initial attempts involved listening to frame.imagePath with a hook.
// But the hook ended up sometimes firing AFTER the image was fully loaded, especially
// when retrieving the image from cache.
// The new solution uses a callback ref, which initializes at the moment the DOM node
// is created, and is thus more reliable.
// IMPORTANT:
// IMPORTANT:
// To make this robust, you have to set a key on the <img> tag itself to force React
// to regenerate it in the case that the image src changes.
// IMPORTANT:
// IMPORTANT:

// Track which images we've already loaded to prevent repeated loading states
const loadedImages = new Set<string>();

export const useImageLoading = (
	imageSrc: string
): [boolean, (node: HTMLImageElement | null) => void] => {
	const [isLoading, setIsLoading] = useState(false);

	const handleImageRef = useCallback(
		(node: HTMLImageElement | null) => {
			if (node) {
				// Check if we've already loaded this image before
				const alreadyLoaded = loadedImages.has(node.src);

				// immediately signal that an image load is starting if it's not already complete.
				if (!node.complete && !alreadyLoaded) {
					setIsLoading(true);
				} else {
					// if it's already complete (possibly from cache), clear the loading flag.
					setIsLoading(false);
				}

				// attach load and error handlers to clear the loading flag.
				node.onload = () => {
					loadedImages.add(node.src);

					// If we load too many images, we probably have a deeper issue. Let's log it.
					if (loadedImages.size > 1000 && loadedImages.size % 100 === 0) {
						Sentry.captureMessage(
							`useImageLoading: Unusually high number of loaded images (${loadedImages.size}). This may indicate excessive frame generation.`,
							'warning'
						);
					}

					setIsLoading(false);
				};
				node.onerror = () => {
					setIsLoading(false);
				};
			}
		},
		[imageSrc]
	);

	return [isLoading, handleImageRef];
};
